/**
 * Media utility functions for Reels video editor
 */

/**
 * Get media type from file
 */
export function getMediaType(file) {
  if (!file || !file.type) return 'unknown';

  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('image/')) return 'image';

  // Check by extension
  const ext = file.name?.split('.').pop()?.toLowerCase();
  const videoExts = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'];
  const audioExts = ['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'];
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

  if (videoExts.includes(ext)) return 'video';
  if (audioExts.includes(ext)) return 'audio';
  if (imageExts.includes(ext)) return 'image';

  return 'unknown';
}

/**
 * Create object URL for file
 */
export function createMediaUrl(file) {
  if (!file) return null;
  return URL.createObjectURL(file);
}

/**
 * Revoke object URL
 */
export function revokeMediaUrl(url) {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * Get video metadata
 */
export function getVideoMetadata(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = createMediaUrl(file);

    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        aspectRatio: video.videoWidth / video.videoHeight,
      });
      revokeMediaUrl(url);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video metadata'));
      revokeMediaUrl(url);
    };

    video.src = url;
    video.load();
  });
}

/**
 * Get audio metadata
 */
export function getAudioMetadata(file) {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    const url = createMediaUrl(file);

    audio.onloadedmetadata = () => {
      resolve({
        duration: audio.duration,
      });
      revokeMediaUrl(url);
    };

    audio.onerror = () => {
      reject(new Error('Failed to load audio metadata'));
      revokeMediaUrl(url);
    };

    audio.src = url;
    audio.load();
  });
}

/**
 * Get image metadata
 */
export function getImageMetadata(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = createMediaUrl(file);

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: img.naturalWidth / img.naturalHeight,
      });
      revokeMediaUrl(url);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image metadata'));
      revokeMediaUrl(url);
    };

    img.src = url;
  });
}

/**
 * Generate video thumbnail at specific time
 */
export function generateVideoThumbnail(file, time = 0, width = 160, height = 90) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const url = createMediaUrl(file);

    video.onloadeddata = () => {
      video.currentTime = time;
    };

    video.onseeked = () => {
      canvas.width = width;
      canvas.height = height;

      // Calculate crop to maintain aspect ratio
      const videoAspect = video.videoWidth / video.videoHeight;
      const canvasAspect = width / height;

      let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;

      if (videoAspect > canvasAspect) {
        sw = video.videoHeight * canvasAspect;
        sx = (video.videoWidth - sw) / 2;
      } else {
        sh = video.videoWidth / canvasAspect;
        sy = (video.videoHeight - sh) / 2;
      }

      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, width, height);

      canvas.toBlob((blob) => {
        resolve(URL.createObjectURL(blob));
        revokeMediaUrl(url);
      }, 'image/jpeg', 0.7);
    };

    video.onerror = () => {
      reject(new Error('Failed to generate thumbnail'));
      revokeMediaUrl(url);
    };

    video.src = url;
    video.load();
  });
}

/**
 * Generate waveform data from audio
 */
export function generateWaveformData(file, samples = 100) {
  return new Promise((resolve, reject) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const audioBuffer = await audioContext.decodeAudioData(e.target.result);
        const channelData = audioBuffer.getChannelData(0);
        const blockSize = Math.floor(channelData.length / samples);
        const waveform = [];

        for (let i = 0; i < samples; i++) {
          let sum = 0;
          const start = i * blockSize;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(channelData[start + j] || 0);
          }
          waveform.push(sum / blockSize);
        }

        // Normalize
        const max = Math.max(...waveform);
        const normalized = waveform.map(v => max > 0 ? v / max : 0);

        resolve(normalized);
        audioContext.close();
      } catch (err) {
        reject(err);
        audioContext.close();
      }
    };

    reader.onerror = () => reject(new Error('Failed to read audio file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Format file size
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Check if file is valid for import
 */
export function isValidMediaFile(file) {
  const type = getMediaType(file);
  return type !== 'unknown';
}

/**
 * Supported video formats
 */
export const SUPPORTED_VIDEO_FORMATS = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
];

/**
 * Supported audio formats
 */
export const SUPPORTED_AUDIO_FORMATS = [
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'audio/aac',
  'audio/mp4',
  'audio/flac',
];

/**
 * Supported image formats
 */
export const SUPPORTED_IMAGE_FORMATS = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

/**
 * All supported formats
 */
export const SUPPORTED_FORMATS = [
  ...SUPPORTED_VIDEO_FORMATS,
  ...SUPPORTED_AUDIO_FORMATS,
  ...SUPPORTED_IMAGE_FORMATS,
];

/**
 * Get accept string for file input
 */
export function getAcceptString(types = ['video', 'audio', 'image']) {
  const formats = [];
  if (types.includes('video')) formats.push(...SUPPORTED_VIDEO_FORMATS);
  if (types.includes('audio')) formats.push(...SUPPORTED_AUDIO_FORMATS);
  if (types.includes('image')) formats.push(...SUPPORTED_IMAGE_FORMATS);
  return formats.join(',');
}
